import { bindActionCreators } from '@reduxjs/toolkit';
import cloneDeep from 'lodash.clonedeep';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import type { DetailedVehicle } from '../../../@types/detailed-vehicle';
import type Item from '../../../@types/item';
import type { LinkTreeItem } from '../../../@types/link-tree';
import type { Category, Property, Tree } from '../../../@types/vehicle-properties';
import { trackPropertiesFilterSearch as _trackPropertiesFilterSearch } from '../../../actions/tracking';
import QueryFilter from '../../../helpers/query-filter';
import Alert from '../../../styled-components/components/Alert';
import InputSearch from '../../../styled-components/components/InputSearch';
import { size } from '../../../styled-components/layout/helpers';
import { escapeRegExpString } from '../../../utils/regExp';
import PropertyComponent from './property';
import PropertyItems from './propertyItems';

export const SEARCH_TAB_ID = 999999999;

const ResultList = styled.div.withConfig({ componentId: 'sc-1sxg6o9-0' })`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${size(3)};
`;

const ResultCategory = styled.div.withConfig({ componentId: 'sc-1sxg6o9-1' })`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${size(2)};
`;

const CategoryLabelList = styled.div.withConfig({ componentId: 'sc-1sxg6o9-2' })`
    --_label-gap: ${size(0.5)};

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--_label-gap);
    font-size: var(--font-size-xsmall-static);
    font-weight: var(--font-weight-medium);
`;

const CategoryLabel = styled.div.withConfig({ componentId: 'sc-1sxg6o9-3' })`
    &:not(:first-child) {
        display: flex;
        align-items: center;
        gap: var(--_label-gap);

        // Chevron icon between category labels
        &::before {
            --_icon-size: ${size(2)};

            content: "";
            flex-shrink: 0;
            display: block;
            width: var(--_icon-size);
            height: var(--_icon-size);
            margin: calc(var(--_icon-size) * -0.5) 0; // Bleed block
            background-color: var(--c-border-main);
            mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M6.374 13.374 4.96 11.96 8.92 8 4.96 4.04l1.414-1.414L11.748 8l-5.374 5.374Z"/></svg>');
            mask-size: 100%;
        }
    }
`;

const createRegExp = (search: string) => new RegExp(`(${escapeRegExpString(search)})`, 'gi');

const filterProperty = (search: string, property: Property) => {
  const { name, value, unit } = property;
  const reg = createRegExp(search);
  return reg.test(name) || (value && reg.test(value.toString())) || (unit && reg.test(unit));
};

const reduceCategory = (search: string, category: Category): Category | undefined => {
  category.properties = category.properties.filter((p) => filterProperty(search, p));
  category.subCategories = category.subCategories
    .flatMap((x) => reduceCategory(search, x))
    .filter(Boolean) as Category[];

  // If both properties and subCategories are empty, filter out this category
  if (!category.properties.length && !category.subCategories.length) {
    return undefined;
  }

  return category;
};

const searchProperties = (search: string, tree: Tree) => {
  return cloneDeep(tree.categories)
    .flatMap((cat) => reduceCategory(search, cat))
    .filter(Boolean) as Category[];
};

interface IProps {
  activeCategoryId: number;
  propertyTree: Tree;
  category?: Category;
  item: DetailedVehicle | Item;
  disblePropertyLinks?: boolean;
}

const CategoryComponent = ({
  activeCategoryId,
  propertyTree,
  category,
  item,
  disblePropertyLinks,
}: IProps) => {
  const { t } = useTranslation('item');
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const ref = useRef(-1);
  const trackPropertiesFilterSearch = bindActionCreators(_trackPropertiesFilterSearch, dispatch);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
  };

  // Track filter search
  useEffect(() => {
    clearTimeout(ref.current);
    ref.current = window.setTimeout(() => {
      if (search) {
        trackPropertiesFilterSearch(item, {
          search,
        });
      }
    }, 1000);
  }, [search]);

  const searchedProperties = useMemo(
    () => (search ? searchProperties(search, propertyTree) : undefined),
    [search, propertyTree],
  );
  const linkTree = useMemo(
    (): { [key: string]: LinkTreeItem | undefined } => ({
      manufacturer: {
        title: t('properties.seoLinks.showAllFromManufacturer', {
          ns: 'item',
          manufacturer: item.manufacturer,
        }),
        queryFilter: QueryFilter.create().add('manufacturer', item.manufacturer),
      },
      colorName: {
        title: t('properties.seoLinks.showAllModelsWithColor', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.properties.colorName,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .add('colorName', item.properties.colorName),
      },
      chassis: {
        title: t('properties.seoLinks.showAllModelsWithChassis', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.properties.chassis,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .add('chassis', item.properties.chassis),
      },
      segment: {
        title: t('properties.seoLinks.showAllModelsWithSegment', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.properties.segment,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .add('properties.segment', item.properties.segment),
      },
      modelSeries: {
        title: t('properties.seoLinks.showAllModels', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries),
      },
      fuelType: {
        title: t('properties.seoLinks.showAllModelsWithFuelType', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.fuelType,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .add('fuelType', item.fuelType),
      },
      drivingWheel: {
        title: t('properties.seoLinks.showAllModelsWithDrivingWheel', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.properties.drivingWheel,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .add('drivingWheel', item.properties.drivingWheel),
      },
      gearboxType: {
        title: t('properties.seoLinks.showAllModelsWithGearbox', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.gearboxType,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .add('gearboxType', item.gearboxType),
      },
      modelYear: {
        title: t('properties.seoLinks.showAllModelsWithModelYear', {
          ns: 'item',
          manufacturer: item.manufacturer,
          model: item.modelSeries,
          property: item.modelYear,
        }),
        queryFilter: QueryFilter.create()
          .add('manufacturer', item.manufacturer)
          .add('modelSeries', item.modelSeries)
          .addRange('modelYear', item.modelYear, item.modelYear),
      },
    }),
    [item],
  );

  return (
    <>
      {activeCategoryId === SEARCH_TAB_ID ? (
        <>
          <InputSearch
            placeholder={t('carDetailsSearch.inputSearch.placeholder', { ns: 'item' })}
            aria-label={t('carDetailsSearch.inputSearch.label', { ns: 'item' })}
            value={search}
            onChange={onChange}
          />
          {search &&
            (searchedProperties?.length ? (
              <ResultList>
                {searchedProperties?.map((cat) => (
                  <Fragment key={cat.id}>
                    {!!cat.properties.length && (
                      <ResultCategory key={cat.id}>
                        <CategoryLabelList>
                          <CategoryLabel>{cat.name}</CategoryLabel>
                        </CategoryLabelList>

                        <div data-wx-datalist="">
                          <ul className="data-list">
                            {cat.properties.map((property) => (
                              <PropertyComponent
                                key={property.key}
                                property={property}
                                link={linkTree?.[property.key]}
                                disblePropertyLinks={disblePropertyLinks}
                              />
                            ))}
                          </ul>
                        </div>
                      </ResultCategory>
                    )}
                    {cat.subCategories.map((subCat) => (
                      <ResultCategory key={subCat.id}>
                        <CategoryLabelList>
                          <CategoryLabel>{cat.name}</CategoryLabel>
                          <CategoryLabel>{subCat.name}</CategoryLabel>
                        </CategoryLabelList>
                        {!!subCat.properties.length && (
                          <div data-wx-datalist="">
                            <ul className="data-list">
                              {subCat.properties.map((property) => (
                                <PropertyComponent
                                  key={property.key}
                                  property={property}
                                  link={linkTree?.[property.key]}
                                  disblePropertyLinks={disblePropertyLinks}
                                />
                              ))}
                            </ul>
                          </div>
                        )}
                      </ResultCategory>
                    ))}
                  </Fragment>
                ))}
              </ResultList>
            ) : (
              <Alert tone="warning">{t('carDetailsSearch.noResults', { ns: 'item' })}</Alert>
            ))}
        </>
      ) : category ? (
        <>
          {!!category.properties.length && (
            <div className="repeat-m-double">
              <PropertyItems
                linkTree={linkTree}
                properties={category.properties}
                disblePropertyLinks={disblePropertyLinks}
              />
            </div>
          )}
          {category.subCategories.map((subCategory, index) => (
            <div key={`${subCategory.name}-${index}`} className="repeat-m-double">
              <h3 className="h5">{subCategory.name}</h3>
              <PropertyItems
                linkTree={linkTree}
                properties={subCategory.properties}
                disblePropertyLinks={disblePropertyLinks}
              />
            </div>
          ))}
        </>
      ) : null}
    </>
  );
};

export default CategoryComponent;
